import { useCallback, useMemo, useRef, useState } from 'react'

import { useMutation } from '@apollo/client/react'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import type { ApolloCache } from '@apollo/client'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { ConversationAfterReply_Conversation } from '../fragments/conversationAfterReply'
import type { ConversationCardData } from '../fragments/conversationCard'
import { ThreadMessage_ConversationMessage } from '../fragments/threadMessage'
import type { ThreadMessageData } from '../fragments/threadMessage'
import type { DMsChannel } from '../types'
import {
  ConversationThreadQueryGQL,
  MESSAGES_PER_PAGE,
} from './useConversationMessagesQuery'

export const ReplyToConversationMutationGQL = graphql(
  /* GraphQL */ `
    mutation ReplyToConversation($input: ReplyToConversationInput!) {
      replyToConversation(input: $input) {
        __typename
        ... on ReplyToConversationSuccess {
          conversation {
            ...ConversationAfterReply_Conversation
          }
          conversationMessage {
            ...ThreadMessage_ConversationMessage
          }
        }
        ... on ConversationReplySendFailedError {
          conversation {
            ...ConversationAfterReply_Conversation
          }
          conversationMessage {
            ...ThreadMessage_ConversationMessage
          }
        }
        ... on ConversationReplyWindowClosedError {
          expiredAt
        }
        ... on MutationError {
          message
        }
      }
    }
  `,
  [ConversationAfterReply_Conversation, ThreadMessage_ConversationMessage],
)

export const SEND_ERROR_MESSAGES = {
  windowClosed:
    "This conversation's 7-day reply window has closed, so your message wasn't sent. You can still reply on Instagram.",
  permission:
    "You don't have permission to reply from this channel. Ask an admin of your organization for access.",
  refresh:
    'Your Instagram channel needs to be reconnected before you can send messages.',
  refreshAskAdmin:
    'Your Instagram channel needs to be reconnected before you can send messages. Ask an admin of your organization to reconnect it.',
  invalid: "This message couldn't be sent. Check it and try again.",
  notFound: 'This conversation is no longer available.',
  unknown: "Your message wasn't sent. Please try again.",
} as const

/** Tracks reconnects started from a reply the channel refused. */
export const RECONNECT_CTA = 'publish-messages-reply-toast-reconnect'

type UseSendMessageReturn = {
  /**
   * Sends `text` as a reply. `true` once the network has the reply; `false`
   * when it was not sent, so the composer puts the text back.
   */
  send: (text: string) => Promise<boolean>
  /** Replies of the open conversation still on their way, oldest first. */
  pendingMessages: ThreadMessageData[]
}

/**
 * Replying in a DM conversation, for the composer under the thread.
 *
 * A reply shows at the end of the thread as soon as it is sent, as a pending
 * message held here rather than in the cache. When the API answers, the
 * stored reply takes its place in the thread's cached messages and the
 * returned conversation updates the list row; a reply the network rejected
 * is stored too and stays in the thread as not sent. Anything else removes
 * the pending message and says why in a toast. When the channel must be
 * reconnected, the toast offers to do it, to whoever may reconnect it.
 *
 * The API serves a mock reply that it does not keep, so a later load of the
 * thread drops it until DM storage lands.
 */
export function useSendMessage(
  conversation: ConversationCardData | undefined,
  /** The conversation's channel, which a reconnect needs. */
  channel: DMsChannel | undefined,
): UseSendMessageReturn {
  const [replyToConversation] = useMutation(ReplyToConversationMutationGQL)
  const refreshChannelConnection = useRefreshChannelConnection()
  const [pending, setPending] = useState<ThreadMessageData[]>([])
  // A reply that may have reached the API without us hearing back. Sending
  // the same text to the same conversation again reuses its key, so the API
  // returns the stored reply rather than sending it twice; any other reply
  // gets a new key.
  const unconfirmedRef = useRef<{
    conversationId: string
    text: string
    clientRef: string
  } | null>(null)

  const send = useCallback(
    async (text: string): Promise<boolean> => {
      if (!conversation) return false
      const conversationId = conversation.id
      const unconfirmed = unconfirmedRef.current
      const clientRef =
        unconfirmed?.conversationId === conversationId &&
        unconfirmed.text === text
          ? unconfirmed.clientRef
          : crypto.randomUUID()
      unconfirmedRef.current = null

      const pendingMessage = buildPendingMessage(conversation, text, clientRef)
      const removePending = (): void =>
        setPending((messages) =>
          messages.filter((message) => message.id !== pendingMessage.id),
        )
      setPending((messages) => [...messages, pendingMessage])

      try {
        const { data } = await replyToConversation({
          variables: {
            input: { clientRef, conversationId, content: { text } },
          },
          update: (cache, { data: updateData }) => {
            const result = updateData?.replyToConversation
            if (
              result?.__typename === 'ReplyToConversationSuccess' ||
              result?.__typename === 'ConversationReplySendFailedError'
            ) {
              addToThread(cache, result.conversationMessage)
            }
            if (result?.__typename === 'ConversationReplyWindowClosedError') {
              closeReplyWindow(cache, conversationId, result.expiredAt)
            }
            // In the same update as the cache write, so the stored reply
            // replaces the pending one without both showing for a frame.
            removePending()
          },
        })

        const result = data?.replyToConversation
        switch (result?.__typename) {
          case 'ReplyToConversationSuccess':
            return true
          case 'ConversationReplySendFailedError':
            return false
          case 'ConversationReplyWindowClosedError':
            toast.error(SEND_ERROR_MESSAGES.windowClosed)
            return false
          case 'ConversationChannelPermissionError':
            toast.error(SEND_ERROR_MESSAGES.permission)
            return false
          case 'ChannelRefreshRequired':
            if (channel && canPerform(channel, 'reconnectChannel')) {
              toast.error(SEND_ERROR_MESSAGES.refresh, {
                action: {
                  label: 'Reconnect',
                  onClick: (): void =>
                    refreshChannelConnection({
                      channel: {
                        id: channel.id,
                        service: channel.service,
                        name: channel.name,
                        apiVersion: channel.apiVersion,
                      },
                      cta: RECONNECT_CTA,
                    }),
                },
              })
            } else {
              toast.error(SEND_ERROR_MESSAGES.refreshAskAdmin)
            }
            return false
          case 'InvalidInputError':
            toast.error(SEND_ERROR_MESSAGES.invalid)
            return false
          default:
            unconfirmedRef.current = { conversationId, text, clientRef }
            toast.error(SEND_ERROR_MESSAGES.unknown)
            return false
        }
      } catch (error) {
        removePending()
        if (isNotFound(error)) {
          toast.error(SEND_ERROR_MESSAGES.notFound)
          return false
        }
        unconfirmedRef.current = { conversationId, text, clientRef }
        toast.error(SEND_ERROR_MESSAGES.unknown)
        return false
      }
    },
    [conversation, channel, replyToConversation, refreshChannelConnection],
  )

  const conversationId = conversation?.id
  const pendingMessages = useMemo(
    () =>
      pending.filter((message) => message.conversationId === conversationId),
    [pending, conversationId],
  )

  return { send, pendingMessages }
}

/**
 * The reply as the thread shows it before the API answers: ours, on its way,
 * placed by the time it was sent.
 */
function buildPendingMessage(
  conversation: ConversationCardData,
  text: string,
  clientRef: string,
): ThreadMessageData {
  const owner = conversation.participants.find(
    (participant) => participant.isOwner,
  )
  return {
    __typename: 'ConversationMessage',
    id: `pending:${clientRef}`,
    conversationId: conversation.id,
    isOwnMessage: true,
    contentType: 'text',
    text,
    publishedAt: null,
    isEdited: false,
    isServiceDeleted: false,
    author: owner?.author ?? {
      name: null,
      username: '',
      avatar: '',
      letterAvatar: null,
    },
    assets: [],
    reactions: [],
    send: { state: 'processing', sentAt: new Date().toISOString() },
  }
}

/**
 * Puts a stored reply at the newest end of its thread's cached messages,
 * which the API returns newest first. The reply comes without a cursor, so
 * its id stands in: paging only ever sends the oldest edge's cursor.
 */
function addToThread(cache: ApolloCache, message: ThreadMessageData): void {
  cache.updateQuery(
    {
      query: ConversationThreadQueryGQL,
      variables: {
        input: { conversationId: message.conversationId },
        first: MESSAGES_PER_PAGE,
        after: null,
      },
    },
    (data) => {
      if (!data) return undefined
      const { edges } = data.conversationMessages
      if (edges.some((edge) => edge.node.id === message.id)) return undefined
      return {
        ...data,
        conversationMessages: {
          ...data.conversationMessages,
          edges: [
            {
              __typename: 'ConversationMessageEdge' as const,
              cursor: message.id,
              node: message,
            },
            ...edges,
          ],
        },
      }
    },
  )
}

/**
 * Records that the reply window has closed, which swaps the composer for the
 * closed-window notice.
 */
function closeReplyWindow(
  cache: ApolloCache,
  conversationId: string,
  expiredAt: string,
): void {
  cache.modify({
    id: cache.identify({
      __typename: 'MessageConversation',
      id: conversationId,
    }),
    fields: {
      replyWindow: (existing) => ({ ...existing, expiresAt: expiredAt }),
    },
  })
}

/** The API reports a conversation it cannot find as a `NOT_FOUND` error. */
function isNotFound(error: unknown): boolean {
  return (
    CombinedGraphQLErrors.is(error) &&
    error.errors.some(
      (graphQLError) => graphQLError.extensions?.code === 'NOT_FOUND',
    )
  )
}
